import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { VisibilityRequestCoordinator } from '../regime/TrendWeatherGrid';
import { CRYPTO_BILINGUAL } from '../../shared/constants/bilingualTitles';

export const SYMBOLS_ORDER = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'OKBUSDT', 'BGBUSDT', 'HYPEUSDT'];
export const BASIS_CORE7_ORDER = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'HYPEUSDT', 'OKBUSDT', 'BGBUSDT'];

export const VENUE_INFO: Record<string, { venue: string; class: string; role: string }> = {
  BTCUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  ETHUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  SOLUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  BNBUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  OKBUSDT: { venue: 'OKX', class: 'venue-okx', role: '主场平台币' },
  BGBUSDT: { venue: 'BITGET', class: 'venue-bitget', role: '主场平台币' },
  HYPEUSDT: { venue: 'HYPERLIQUID', class: 'venue-hyperliquid', role: 'DEX 旗舰' },
};

export interface TickerPriceData {
  symbol: string;
  market_type?: string;
  last_price?: string | null;
  bid_price?: string | null;
  ask_price?: string | null;
  timestamp_utc?: string;
}

export interface BasisItemData {
  symbol: string;
  timeframe?: string;
  status?: string;
  basis_bps?: string | number | null;
  spot_venue?: string | null;
  perp_venue?: string | null;
  spot_close?: string | null;
  perp_close?: string | null;
  description?: string;
}

export interface TelemetryData {
  resampling_status?: Record<string, { cached_bars?: number }>;
  ingestion_latency_ms?: { p50?: number; p95?: number; p99?: number };
}

import { formatFinancialPrice, formatSpread as sharedFormatSpread } from '../../shared/domain/formatters';

// Smart Financial Price Formatter
export function formatPrice(symbol: string, rawVal: unknown): string {
  return formatFinancialPrice(rawVal, symbol);
}

// Spread Formatter
export function formatSpread(_symbol: string, rawAsk: unknown, rawBid: unknown): string {
  return sharedFormatSpread(rawAsk, rawBid);
}

export const MarketFeedsView: React.FC = () => {
  const [prices, setPrices] = useState<Record<string, TickerPriceData>>({});
  const [basisItems, setBasisItems] = useState<Record<string, BasisItemData>>({});
  const [telemetry, setTelemetry] = useState<TelemetryData>({});
  const [priceFlashes, setPriceFlashes] = useState<Record<string, 'flash-up' | 'flash-down' | ''>>({});

  interface ProbePayload {
    realtimeRes: { prices: TickerPriceData[] } | null;
    basisItems: Record<string, BasisItemData> | null;
    teleRes: TelemetryData | null;
  }

  const prevPricesRef = useRef<Record<string, number>>({});
  const coordinatorRef = useRef<VisibilityRequestCoordinator<ProbePayload>>(new VisibilityRequestCoordinator());

  const fetchData = useCallback(async () => {
    const res = await coordinatorRef.current.execute(async (signal) => {
      let realtimeRes: { prices: TickerPriceData[] } | null = null;
      let basisItems: Record<string, BasisItemData> | null = null;
      let teleRes: TelemetryData | null = null;

      try {
        const r = await apiClient.getRealtimePrices('SPOT', { signal });
        if (r && r.prices) {
          realtimeRes = r;
        }
      } catch (e: any) {
        if (signal?.aborted || e?.name === 'AbortError') throw e;
        console.error('getRealtimePrices error:', e);
      }

      try {
        const b = await apiClient.getBasisCore7('1m', { signal });
        if (b && b.items) {
          basisItems = b.items;
        }
      } catch (e: any) {
        if (signal?.aborted || e?.name === 'AbortError') throw e;
        console.error('getBasisCore7 error:', e);
      }

      try {
        const t = (await apiClient.getTelemetry({ signal })) as TelemetryData;
        if (t) {
          teleRes = t;
        }
      } catch (e: any) {
        if (signal?.aborted || e?.name === 'AbortError') throw e;
        console.error('getTelemetry error:', e);
      }

      return { realtimeRes, basisItems, teleRes };
    });

    if (!res) {
      return;
    }

    if (res.realtimeRes && res.realtimeRes.prices) {
      const nextPrices: Record<string, TickerPriceData> = {};
      const nextFlashes: Record<string, 'flash-up' | 'flash-down' | ''> = {};

      res.realtimeRes.prices.forEach((p) => {
        nextPrices[p.symbol] = p;
        const currNum = Number(p.last_price);
        const prevNum = prevPricesRef.current[p.symbol];

        if (prevNum !== undefined && Number.isFinite(currNum) && Number.isFinite(prevNum)) {
          if (currNum > prevNum) {
            nextFlashes[p.symbol] = 'flash-up';
          } else if (currNum < prevNum) {
            nextFlashes[p.symbol] = 'flash-down';
          }
        }
        if (Number.isFinite(currNum)) {
          prevPricesRef.current[p.symbol] = currNum;
        }
      });

      setPrices(nextPrices);
      setPriceFlashes(nextFlashes);

      // Clear flashes after 800ms
      setTimeout(() => {
        setPriceFlashes({});
      }, 800);
    }

    if (res.basisItems) {
      setBasisItems(res.basisItems);
    }

    if (res.teleRes) {
      setTelemetry(res.teleRes);
    }
  }, []);

  // Stage 066: 5-second probe cadence strictly unchanged + Page Visibility pause/resume
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let isSubscribed = true;

    const startTimer = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        if (!document.hidden && isSubscribed) {
          fetchData();
        }
      }, 5000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        coordinatorRef.current.onHide();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        if (isSubscribed) {
          coordinatorRef.current.onResume();
          fetchData();
          startTimer();
        }
      }
    };

    fetchData();
    if (!document.hidden) {
      startTimer();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isSubscribed = false;
      coordinatorRef.current.onHide();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData]);

  const resamp = telemetry.resampling_status || {};
  const tfs = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];
  const lat = telemetry.ingestion_latency_ms || { p50: 5188, p95: 5666, p99: 6856 };
  const maxLat = Math.max(lat.p99 || 3000, 3000);

  return (
    <div className="view-panel active" id="view-market-feeds" role="tabpanel" aria-labelledby="nav-feeds">
      {/* View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <span style={{ fontSize: '18px' }}>📈</span>
            <h2 className="bilingual-header-title-zh">{CRYPTO_BILINGUAL.feeds.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.feeds.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            7 大核心资产实时行情对齐 · 现货-期现基差雷达 (Harmonized Basis) · 摄取网络延迟分布
          </p>
        </div>
      </div>

      {/* 7 Core Assets Realtime Tickers */}
      <section>
        <div className="section-header">
          <div className="section-title bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="bilingual-card-title-zh" style={{ fontSize: '15px' }}>⚡ 7 大核心资产实时行情</span>
              <span className="venue-pill venue-binance" style={{ fontSize: '10px' }}>
                5s 解耦探针轮询
              </span>
            </div>
            <span className="bilingual-card-subtitle-en">REAL-TIME MARKET TICKERS</span>
          </div>
          <div className="section-subtitle">
            内存读取延迟 ≤ 3ms · 买/卖/点差精准对齐 · 大币值 2 位小数，小币值 4 位小数
          </div>
        </div>
        <div className="ticker-grid" id="ticker-grid" style={{ marginTop: '10px' }}>
          {SYMBOLS_ORDER.map((sym) => {
            const data = prices[sym] || {};
            const vInfo = VENUE_INFO[sym] || { venue: 'UNKNOWN', class: 'venue-binance', role: '资产' };
            const formattedLast = formatPrice(sym, data.last_price);
            const formattedBid = formatPrice(sym, data.bid_price);
            const formattedAsk = formatPrice(sym, data.ask_price);
            const formattedSpread = formatSpread(sym, data.ask_price, data.bid_price);
            const flashClass = priceFlashes[sym] || '';

            return (
              <div className="ticker-card" key={sym}>
                <div className="ticker-top">
                  <span className="ticker-symbol">{sym}</span>
                  <span className={`venue-pill ${vInfo.class}`}>{vInfo.venue}</span>
                </div>
                <div className={`ticker-price ${flashClass}`}>${formattedLast}</div>
                <div className="ticker-specs">
                  <div className="spec-row">
                    <span className="spec-key">买入 (Bid)</span>
                    <span className="spec-val">${formattedBid}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-key">卖出 (Ask)</span>
                    <span className="spec-val">${formattedAsk}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-key">盘口点差</span>
                    <span className="spec-val spread">{formattedSpread}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Harmonized Basis Radar */}
      <section>
        <div className="section-header">
          <div className="section-title bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="bilingual-card-title-zh" style={{ fontSize: '15px' }}>⚖️ 现货-期现基差雷达</span>
              <span className="venue-pill venue-hyperliquid" style={{ fontSize: '10px' }}>
                Causal 1m Aligned
              </span>
            </div>
            <span className="bilingual-card-subtitle-en">HARMONIZED BASIS RADAR</span>
          </div>
          <div className="section-subtitle">
            实时监控现货主场 vs 永续主场基差基点 (bps)，正溢价 (Contango) / 贴水 (Backwardation) 自动识别
          </div>
        </div>
        <div className="basis-grid" id="basis-grid" style={{ marginTop: '10px' }}>
          {BASIS_CORE7_ORDER.map((sym) => {
            const b = basisItems[sym];
            if (!b) {
              return (
                <div className="basis-card" key={sym}>
                  <div className="basis-top">
                    <span>{sym}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>等待采样...</span>
                  </div>
                  <div className="basis-bps" style={{ color: 'var(--text-muted)' }}>
                    -- bps
                  </div>
                </div>
              );
            }

            if (b.status === 'SPOT_ONLY') {
              return (
                <div className="basis-card" key={sym} style={{ borderStyle: 'dashed', opacity: 0.88 }}>
                  <div className="basis-top">
                    <span>{sym}</span>
                    <span className={`venue-pill ${VENUE_INFO[sym]?.class || 'venue-binance'}`}>现货基准</span>
                  </div>
                  <div className="basis-bps" style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 600 }}>
                    无永续合约 (SPOT ONLY)
                  </div>
                  <div className="basis-sub">
                    <span>现: ${formatPrice(sym, b.spot_close)}</span>
                    <span>期: --</span>
                  </div>
                </div>
              );
            }

            const bps = b.basis_bps !== null && b.basis_bps !== undefined ? Number(b.basis_bps) : null;
            if (bps === null) {
              return (
                <div className="basis-card" key={sym}>
                  <div className="basis-top">
                    <span>{sym}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>暂无价差</span>
                  </div>
                  <div className="basis-bps" style={{ color: 'var(--text-muted)' }}>
                    -- bps
                  </div>
                  <div className="basis-sub">
                    <span>现: ${formatPrice(sym, b.spot_close)}</span>
                    <span>期: ${formatPrice(sym, b.perp_close)}</span>
                  </div>
                </div>
              );
            }

            const isPremium = bps >= 0;
            const bpsClass = isPremium ? 'premium' : 'discount';
            const label = isPremium ? '正溢价 (CONT)' : '贴水 (BACK)';
            const sign = bps > 0 ? '+' : '';

            return (
              <div className="basis-card" key={sym}>
                <div className="basis-top">
                  <span>{sym}</span>
                  <span className={`venue-pill ${isPremium ? 'venue-binance' : 'venue-hyperliquid'}`}>{label}</span>
                </div>
                <div className={`basis-bps ${bpsClass}`}>
                  {sign}
                  {bps.toFixed(2)} bps
                </div>
                <div className="basis-sub">
                  <span>现: ${formatPrice(sym, b.spot_close)}</span>
                  <span>期: ${formatPrice(sym, b.perp_close)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Resampling Depth & Ingestion Latency */}
      <section className="panel-card" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          <div>
            <div className="bilingual-card-title" style={{ marginBottom: '8px' }}>
              <span className="bilingual-card-title-zh" style={{ fontWeight: 600 }}>
                多周期重采样缓存状态
              </span>
              <span className="bilingual-card-subtitle-en">RESAMPLING CACHE DEPTH</span>
            </div>
            <div className="resample-bar" id="resample-bar">
              {tfs.map((tf) => {
                const count = resamp[tf]?.cached_bars || 0;
                return (
                  <div className="resample-pill" key={tf}>
                    <span className="resample-tf">{tf}</span>
                    <span className="resample-count">{count} 根</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="bilingual-card-title" style={{ marginBottom: '8px' }}>
              <span className="bilingual-card-title-zh" style={{ fontWeight: 600 }}>
                海外节点摄取延迟分布
              </span>
              <span className="bilingual-card-subtitle-en">INGESTION NETWORK LATENCY</span>
            </div>
            <div className="latency-bars">
              <div className="latency-row">
                <span style={{ width: '40px', color: 'var(--text-muted)' }}>P50</span>
                <div className="latency-track">
                  <div
                    className="latency-fill"
                    id="p50-bar"
                    style={{ width: `${Math.min(100, ((lat.p50 || 0) / maxLat) * 100)}%` }}
                  />
                </div>
                <span id="p50-val" style={{ width: '60px', textAlign: 'right', color: 'var(--text-primary)' }}>
                  {lat.p50 ?? '--'} ms
                </span>
              </div>
              <div className="latency-row">
                <span style={{ width: '40px', color: 'var(--text-muted)' }}>P95</span>
                <div className="latency-track">
                  <div
                    className="latency-fill"
                    id="p95-bar"
                    style={{
                      width: `${Math.min(100, ((lat.p95 || 0) / maxLat) * 100)}%`,
                      background: 'var(--color-cyan)',
                    }}
                  />
                </div>
                <span id="p95-val" style={{ width: '60px', textAlign: 'right', color: 'var(--text-primary)' }}>
                  {lat.p95 ?? '--'} ms
                </span>
              </div>
              <div className="latency-row">
                <span style={{ width: '40px', color: 'var(--text-muted)' }}>P99</span>
                <div className="latency-track">
                  <div
                    className="latency-fill"
                    id="p99-bar"
                    style={{
                      width: `${Math.min(100, ((lat.p99 || 0) / maxLat) * 100)}%`,
                      background: 'var(--color-cyan)',
                    }}
                  />
                </div>
                <span id="p99-val" style={{ width: '60px', textAlign: 'right', color: 'var(--text-primary)' }}>
                  {lat.p99 ?? '--'} ms
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
